import { t } from "@hakili/i18n";
import { Button, Card, Field, GuardedButton } from "@hakili/ui";
import { type ReactElement, useState } from "react";

import type { Dealership, DoorResult } from "../session/useSession.js";

/** The four facts the dealership's proforma prints, in the order it prints them. */
const FIELDS = [
  { key: "rccm", label: "dealer.profil.mentionRccm", long: false },
  { key: "nif", label: "dealer.profil.mentionNif", long: false },
  { key: "paymentTerms", label: "dealer.profil.mentionConditions", long: true },
  { key: "warranty", label: "dealer.profil.mentionGarantie", long: true },
] as const;

type Key = (typeof FIELDS)[number]["key"];

/**
 * `A-DLR` Arc C, `C0` (architect ruling « ARC C M0 », R1 and R2): **what the dealership's proforma prints of the
 * dealership, entered by its manager on its own fiche.** RCCM, NIF, payment terms and warranty are the
 * dealership's own words, printed verbatim and attributed to it; Hakili authors none. Written by `PUT
 * /api/v1/me/dealership/proforma-details` under `gererEquipeConcession`, like the address beside it; an employee
 * reads them and the control is greyed with its reason (I19). A missing mention is named, because the issue door
 * will refuse until it is there.
 */
export function ProformaDetailsBlock(props: {
  readonly dealership: Dealership | null;
  readonly canWrite: boolean;
  readonly write: (input: Record<Key, string>) => Promise<DoorResult<Dealership>>;
  readonly onSaved: (dealership: Dealership) => void;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<Key, string>>({
    rccm: "",
    nif: "",
    paymentTerms: "",
    warranty: "",
  });
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const missing = FIELDS.filter((field) => props.dealership?.[field.key] === null).map((field) =>
    t(field.label),
  );

  return (
    <Card>
      <div data-testid="profil-mentions">
        <h2 className="text-base font-semibold text-gray-900">{t("dealer.profil.mentionsTitre")}</h2>
        <p className="mt-1 text-sm text-gray-500">{t("dealer.profil.mentionsMots")}</p>
        {props.dealership === null ? null : (
          <dl className="mt-3 text-sm text-gray-700">
            {FIELDS.map((field) => (
              <div key={field.key} className="py-1">
                <dt className="text-gray-500">{t(field.label)}</dt>
                <dd className="font-medium text-gray-900" data-testid={`profil-mention-${field.key}`}>
                  {props.dealership?.[field.key] ?? t("dealer.profil.nonRenseigne")}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {missing.length === 0 || props.dealership === null ? null : (
          <p className="mt-2 text-sm text-amber-700" data-testid="profil-mentions-absentes">
            {t("dealer.profil.mentionsAbsentes").split("{liste}").join(missing.join(", "))}
          </p>
        )}
        {editing ? (
          <div className="mt-3 grid gap-3" data-testid="profil-mentions-formulaire">
            {FIELDS.map((field) => (
              <Field key={field.key} label={t(field.label)} htmlFor={`mention-${field.key}`}>
                {field.long ? (
                  <textarea
                    id={`mention-${field.key}`}
                    rows={3}
                    maxLength={500}
                    value={draft[field.key]}
                    onChange={(event) => {
                      setDraft({ ...draft, [field.key]: event.target.value });
                    }}
                    data-testid={`profil-mention-saisie-${field.key}`}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                ) : (
                  <input
                    id={`mention-${field.key}`}
                    maxLength={40}
                    value={draft[field.key]}
                    onChange={(event) => {
                      setDraft({ ...draft, [field.key]: event.target.value });
                    }}
                    data-testid={`profil-mention-saisie-${field.key}`}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                )}
              </Field>
            ))}
            <div className="flex gap-2">
              <Button
                variant="primary"
                data-testid="profil-mentions-enregistrer"
                onClick={() => {
                  if (busy) return;
                  setBusy(true);
                  setRefusal(null);
                  void props
                    .write({
                      rccm: draft.rccm.trim(),
                      nif: draft.nif.trim(),
                      paymentTerms: draft.paymentTerms.trim(),
                      warranty: draft.warranty.trim(),
                    })
                    .then((answer) => {
                      setBusy(false);
                      if (answer.ok) {
                        props.onSaved(answer.value);
                        setEditing(false);
                      } else {
                        setRefusal(answer.message);
                      }
                    });
                }}
              >
                {t("dealer.profil.mentionsEnregistrer")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(false);
                  setRefusal(null);
                }}
                data-testid="profil-mentions-annuler"
              >
                {t("common.action.annuler")}
              </Button>
            </div>
            {refusal === null ? null : (
              <p role="alert" className="text-sm text-rose-600" data-testid="profil-mentions-refus">
                {refusal}
              </p>
            )}
          </div>
        ) : props.canWrite ? (
          <div className="mt-3">
            <Button
              variant="secondary"
              onClick={() => {
                setDraft({
                  rccm: props.dealership?.rccm ?? "",
                  nif: props.dealership?.nif ?? "",
                  paymentTerms: props.dealership?.paymentTerms ?? "",
                  warranty: props.dealership?.warranty ?? "",
                });
                setEditing(true);
              }}
              data-testid="profil-mentions-modifier"
            >
              {t("dealer.profil.mentionsModifier")}
            </Button>
          </div>
        ) : (
          <div className="mt-3">
            <GuardedButton
              disabled
              reason={t("dealer.profil.modifierRefus")}
              variant="secondary"
              testId="profil-mentions-modifier"
            >
              {t("dealer.profil.mentionsModifier")}
            </GuardedButton>
          </div>
        )}
      </div>
    </Card>
  );
}
